import React, { useEffect } from "react";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, MapPin, Plus } from "lucide-react";
import { userAddressSchema } from "@/schemas/userAddressSchema";
import { toast } from "sonner";
import { listUserShippingAddresses } from "@/graphql/queries";
import { useAppSelector } from "@/store/hooks";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { parseJSONField } from "@/utils/helper/parseJSON";
import ls from "@/utils/SecureLS";
import { createUserShippingAddress, updateUserShippingAddress } from "@/graphql/mutations";
import { UserShippingAddress } from "@/types/AppTypes";
import { v4 as uuidv4 } from "uuid";
import { setUser } from "@/store/slices/authSlice";
import { ScrollArea } from "../ui/scroll-area";

type AddressFormValues = z.infer<typeof userAddressSchema>;

const UserAddress = () => {
  const userLs = ls.get("user");
  const user = userLs ? JSON.parse(userLs) : null;

  const [userShippingAddress, setUserShippingAddress] = useState<UserShippingAddress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingAddress, setEditingAddress] = useState<any | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(userAddressSchema),
    defaultValues: {
      name: "",
      street: "",
      suite: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
    },
  });

  useEffect(() => {
    const fetchUserShippingAddress = async (userID: string) => {
      setIsFetchingAddress(true);
      try {
        const userShippingAddresses = await executeGraphQLMutation({
          query: listUserShippingAddresses,
          variables: {
            filter: {
              user_id: { eq: userID },
            },
          },
        });

        if (userShippingAddresses?.items?.[0]) {
          setUserShippingAddress(userShippingAddresses.items[0]);
        }
      } catch (error) {
        console.error("Failed to fetch shipping address:", error);
      }finally{
        setIsFetchingAddress(false);
      }
    };
    if (user) {
      fetchUserShippingAddress(user?.user_id || "");
    }

  }, []);

  const handleEdit = (address: any) => {
    setEditingAddress(address);
    form.reset({
      name: address?.name || "",
      street: address?.street,
      suite: address?.suite || "",
      city: address?.city || "",
      state: address?.state || "",
      postal_code: address?.postal_code || "",
      country: address?.country || "",
    });
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    form.reset({
      name: "",
      street: "",
      suite: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
    });
    setIsDialogOpen(true);
  };

  const onSubmit = async (data: AddressFormValues) => {
    setIsLoading(true);
    try {
      if (editingAddress) {
        const filteredAddresses = userShippingAddress?.shipping_address?.filter((addr: any) => {
          const parsedAddress = parseJSONField(addr);
          return parsedAddress.name !== editingAddress.name;
        });
        const updatedAddresses = [...(filteredAddresses || []), JSON.stringify(data)];

        const isUpdated = await executeGraphQLMutation({
          query: updateUserShippingAddress,
          variables: {
            input: {
              id: userShippingAddress?.id,
              shipping_address: updatedAddresses,
            },
          },
        });

        if (isUpdated) {
          toast.success("Address updated", {
            description: "Your address has been successfully updated.",
          });
        } else {
          toast.error("Failed to update address.", {
            description: "Failed to update address. Please try again.",
          });
        }
      } else {
        if (
          !userShippingAddress?.shipping_address ||
          userShippingAddress?.shipping_address?.length === 0
        ) {
          const addNewAddress = await executeGraphQLMutation({
            query: createUserShippingAddress,
            variables: {
              input: {
                id: uuidv4(),
                user_id: user?.user_id,
                shipping_address: [JSON.stringify(data)],
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
            },
          });
          if (addNewAddress) {
            setUserShippingAddress(addNewAddress);
            toast.success("Address added", {
              description: "Your new address has been successfully added.",
            });
          } else {
            toast.error("Failed to add address.", {
              description: "Failed to add address. Please try again.",
            });
          }
        } else {
          const addNewAddress = await executeGraphQLMutation({
            query: updateUserShippingAddress,
            variables: {
              input: {
                id: userShippingAddress?.id,
                shipping_address: [...userShippingAddress?.shipping_address, JSON.stringify(data)],
              },
            },
          });
          if (addNewAddress) {
            setUserShippingAddress(addNewAddress);
            toast.success("Address added", {
              description: "Your new address has been successfully added.",
            });
          } else {
            toast.error("Failed to add address.", {
              description: "Failed to add address. Please try again.",
            });
          }
        }

        setIsDialogOpen(false);
      }
    } catch (error) {
      toast.error("Failed to save address.", {
        description: "Failed to save address. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (address: any) => {
    toast.loading("Deleting address...")
    const filteredAddresses = userShippingAddress?.shipping_address?.filter((addr: any) => {
      const parsedAddress = parseJSONField(addr);
      return parsedAddress.name !== address.name;
    });
 
    const isUpdated = await executeGraphQLMutation({
      query: updateUserShippingAddress,
      variables: {
        input: {
          id: userShippingAddress?.id,
          shipping_address: filteredAddresses,
        },
      },
    });

    if (isUpdated) {
      toast.dismiss()
      toast.success("Address deleted successfully");
      setUserShippingAddress((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          shipping_address: filteredAddresses,
        };
      });
    } else {
      toast.dismiss()
      toast.error("Failed to delete address.", {
        description: "Failed to delete address. Please try again.",
      });
    }
  };
  return (
    <Card className="w-full h-full">
      <CardHeader>
        <CardTitle>Your Addresses</CardTitle>
        <CardDescription>Manage your shipping and billing addresses</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
      { isFetchingAddress ? <div className="flex justify-center items-center h-full">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div> : <ScrollArea className="h-[500px] mb-4">
          {userShippingAddress?.shipping_address?.map((address) => {
            const parsedAddress = parseJSONField(address);
            return (
              <div key={parsedAddress?.name} className="border rounded-lg p-4 my-6">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <h3 className="font-semibold">{parsedAddress?.name} Address</h3>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(parsedAddress)}>
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(parsedAddress)}
                      className="bg-red-500 text-white hover:bg-red-600 hover:text-white"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p className="font-medium text-black">
                    {parsedAddress?.first_name} {parsedAddress?.last_name}
                  </p>
                  <p>{parsedAddress?.street}</p>
                  {parsedAddress?.suite && <p>{parsedAddress?.suite}</p>}
                  <p>
                    {parsedAddress?.city}, {parsedAddress?.state} {parsedAddress?.postal_code}
                  </p>
                  <p>{parsedAddress?.country}</p>
                </div>
              </div>
            );
          })}
        </ScrollArea>}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-gray-800" onClick={handleAddNew}>
              <Plus className="mr-2 h-4 w-4" />
              Add New Address
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingAddress ? "Edit Address" : "Add New Address"}</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Street Address</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="suite"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Suite/Apt (Optional)</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="postal_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ZIP Code</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-black text-white hover:bg-gray-800"
                    disabled={isLoading}
                  >
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingAddress ? "Update" : "Add"} Address
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UserAddress;
